/**
 * Thin fetch wrapper for the rd-mcp REST API. All admin / portal calls
 * funnel through here so we have one place to attach the Bearer token,
 * map errors to a typed shape, and surface 401 → log the user out.
 *
 * We DO NOT touch /mcp/ from the SPA - that's Claude's surface only.
 */

const BASE = (import.meta.env.VITE_API_BASE as string) ?? "";

export type ApiError = {
  status: number;
  message: string;
  detail?: unknown;
};

let unauthorizedHandler: (() => void) | null = null;

export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

/**
 * Best-effort `POST /api/auth/logout` while the document is unloading (tab close,
 * refresh, etc.). Uses `fetch(..., { keepalive: true })` so the request can
 * outlive the page; normal `fetch` is often aborted on unload.
 *
 * Call only from `pagehide` when **`event.persisted` is false** (skip bfcache).
 */
export function logoutOnUnloadKeepalive(token: string): void {
  if (!token) return;
  try {
    void fetch(`${BASE}/api/auth/logout`, {
      method: "POST",
      keepalive: true,
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* ignore */
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const { token, headers, ...rest } = init;
  const finalHeaders = new Headers(headers ?? {});
  if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  if (rest.body && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE}${path}`, { ...rest, headers: finalHeaders });

  if (res.status === 204) return undefined as T;

  let body: unknown = undefined;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    body = await res.json().catch(() => undefined);
  } else {
    body = await res.text().catch(() => undefined);
  }

  if (!res.ok) {
    if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
    const message =
      (typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : undefined) ?? `${res.status} ${res.statusText}`;
    const err: ApiError = { status: res.status, message, detail: body };
    throw err;
  }
  return body as T;
}

// ---------- typed endpoints ----------

export type LoginResponse = { access_token: string; token_type: string };
export type Me = {
  id: number;
  username: string;
  is_admin: boolean;
  disabled: boolean;
  last_seen_at?: string | null;
  rate_limit_per_minute?: number | null;
  query_timeout_seconds?: number | null;
  /** Admin-only: filled on `GET /api/admin/users` */
  mcp_invocations_today?: number | null;
  /** Admin-only: `online` | `away` | `offline` */
  online_status?: string | null;
};
export type Grant = { rds_id: number; schema_name: string };
export type RdsRegistration = {
  id: number;
  display_name: string;
  host: string;
  port: number;
  secret_arn: string;
  schemas: string[];
  has_context?: boolean;
};

export type AddRdsResponse = { id: number; schemas: string[]; created: boolean };
export type User = Me;

export type ContextBehavior = {
  default_mode: string;
  use_default: boolean;
  default_pointer_text: string;
  custom_pointer_text: string;
  effective_pointer_text: string;
  valid_modes: string[];
};

export type PortalLimitDefaults = {
  tools_per_minute_default: number;
  query_timeout_seconds_default: number;
};

export type OAuthClient = {
  id: number;
  client_id: string;
  client_name: string;
  redirect_uris: string[];
  is_confidential: boolean;
  disabled: boolean;
  created_at?: string | null;
};

/** Returned only by create / rotate — `client_secret` is shown exactly once. */
export type OAuthClientCreated = OAuthClient & { client_secret?: string | null };

export type Role = {
  id: number;
  name: string;
  description?: string | null;
  confers_admin: boolean;
  is_protected: boolean;
  member_count: number;
  grant_count: number;
};

export type RoleRef = { id: number; name: string; confers_admin: boolean };
export type RoleMember = { id: number; username: string };

export type MyFile = { key: string; name: string; size: number; last_modified: string | null };

export type Agent = {
  id: number;
  name: string;
  disabled: boolean;
  client_id: string | null;
  role_count: number;
  active_token_count: number;
  created_at?: string | null;
};

/** Returned by create / rotate-secret — `client_secret` shown exactly once. */
export type AgentCreated = Agent & { client_secret?: string | null };

export type AgentToken = {
  id: number;
  label: string | null;
  expires_at: string | null;
  revoked: boolean;
  last_used_at: string | null;
  created_at?: string | null;
};

/** Returned by create-token — `token` (the PAT) shown exactly once. */
export type AgentTokenCreated = AgentToken & { token?: string | null };

export const api = {
  login: (username: string, password: string) =>
    apiFetch<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  refresh: (token: string) =>
    apiFetch<LoginResponse>("/api/auth/refresh", { method: "POST", token }),
  /** Clears server-side presence; call before dropping the JWT locally. */
  logout: (token: string) =>
    apiFetch<void>("/api/auth/logout", { method: "POST", token }),
  me: (token: string) => apiFetch<Me>("/api/me", { token }),
  myGrants: (token: string) => apiFetch<Grant[]>("/api/me/grants", { token }),

  // My Files (private S3 exports)
  listMyFiles: (token: string) => apiFetch<MyFile[]>("/api/me/files", { token }),
  /**
   * Streams the file through the authenticated API and returns the bytes as a
   * Blob (plus the server-suggested filename). No presigned S3 URL is exposed,
   * so there is no shareable bearer link to leak.
   */
  downloadMyFile: async (token: string, key: string): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(`${BASE}/api/me/files/download?key=${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      if (res.status === 401 && unauthorizedHandler) unauthorizedHandler();
      const err: ApiError = { status: res.status, message: `${res.status} ${res.statusText}` };
      throw err;
    }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") ?? "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m?.[1] ?? key.split("/").pop() ?? "export.csv";
    return { blob, filename };
  },
  deleteMyFile: (token: string, key: string) =>
    apiFetch<{ deleted: boolean; key: string }>(`/api/me/files?key=${encodeURIComponent(key)}`, {
      method: "DELETE",
      token,
    }),

  // Admin
  getPortalLimitDefaults: (token: string) =>
    apiFetch<PortalLimitDefaults>("/api/admin/portal-limit-defaults", { token }),
  listUsers: (token: string) => apiFetch<User[]>("/api/admin/users", { token }),
  createUser: (token: string, body: { username: string; password: string; is_admin: boolean }) =>
    apiFetch<User>("/api/admin/users", { method: "POST", token, body: JSON.stringify(body) }),
  patchUser: (
    token: string,
    id: number,
    body: Partial<{
      is_admin: boolean;
      disabled: boolean;
      username: string;
      rate_limit_per_minute: number | null;
      query_timeout_seconds: number | null;
    }>,
  ) => apiFetch<User>(`/api/admin/users/${id}`, { method: "PATCH", token, body: JSON.stringify(body) }),
  deleteUser: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; username: string }>(`/api/admin/users/${id}`, {
      method: "DELETE",
      token,
    }),
  setPassword: (token: string, id: number, new_password: string) =>
    apiFetch<{ ok: boolean }>(`/api/admin/users/${id}/password`, {
      method: "POST",
      token,
      body: JSON.stringify({ new_password }),
    }),

  listRds: (token: string) => apiFetch<RdsRegistration[]>("/api/admin/rds", { token }),
  createRds: (
    token: string,
    body: { display_name: string; host: string; port: number; secret_arn: string },
  ) =>
    apiFetch<AddRdsResponse>("/api/admin/rds", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),
  refreshRdsSchemas: (token: string, id: number) =>
    apiFetch<{ schemas: string[] }>(`/api/admin/rds/${id}/refresh-schemas`, {
      method: "POST",
      token,
    }),
  deleteRds: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; display_name: string }>(`/api/admin/rds/${id}`, {
      method: "DELETE",
      token,
    }),

  // OAuth clients (Claude custom connector credentials)
  listOAuthClients: (token: string) =>
    apiFetch<OAuthClient[]>("/api/admin/oauth/clients", { token }),
  createOAuthClient: (
    token: string,
    body: { client_name: string; redirect_uris?: string[] | null; is_confidential?: boolean },
  ) =>
    apiFetch<OAuthClientCreated>("/api/admin/oauth/clients", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),
  rotateOAuthClientSecret: (token: string, id: number) =>
    apiFetch<OAuthClientCreated>(`/api/admin/oauth/clients/${id}/secret`, {
      method: "POST",
      token,
    }),
  deleteOAuthClient: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; client_id: string }>(
      `/api/admin/oauth/clients/${id}`,
      { method: "DELETE", token },
    ),

  // RBAC: roles, role grants, role members, and user↔role membership.
  myRoles: (token: string) => apiFetch<RoleRef[]>("/api/me/roles", { token }),
  listRoles: (token: string) => apiFetch<Role[]>("/api/admin/roles", { token }),
  createRole: (token: string, body: { name: string; description?: string | null }) =>
    apiFetch<Role>("/api/admin/roles", { method: "POST", token, body: JSON.stringify(body) }),
  patchRole: (token: string, id: number, body: { name?: string; description?: string | null }) =>
    apiFetch<Role>(`/api/admin/roles/${id}`, { method: "PATCH", token, body: JSON.stringify(body) }),
  deleteRole: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number; name: string }>(`/api/admin/roles/${id}`, {
      method: "DELETE",
      token,
    }),
  getRoleGrants: (token: string, roleId: number) =>
    apiFetch<Grant[]>(`/api/admin/roles/${roleId}/grants`, { token }),
  putRoleGrants: (token: string, roleId: number, grants: Grant[]) =>
    apiFetch<{ ok: boolean }>(`/api/admin/roles/${roleId}/grants`, {
      method: "PUT",
      token,
      body: JSON.stringify({ grants }),
    }),
  getRoleMembers: (token: string, roleId: number) =>
    apiFetch<RoleMember[]>(`/api/admin/roles/${roleId}/members`, { token }),
  getUserRoles: (token: string, userId: number) =>
    apiFetch<RoleRef[]>(`/api/admin/users/${userId}/roles`, { token }),
  putUserRoles: (token: string, userId: number, roleIds: number[]) =>
    apiFetch<RoleRef[]>(`/api/admin/users/${userId}/roles`, {
      method: "PUT",
      token,
      body: JSON.stringify({ role_ids: roleIds }),
    }),

  // MCP context (Global + per-database)
  getServerContext: (token: string) =>
    apiFetch<{ value: string }>("/api/admin/server-context", { token }),
  putServerContext: (token: string, value: string) =>
    apiFetch<{ ok: boolean }>("/api/admin/server-context", {
      method: "PUT",
      token,
      body: JSON.stringify({ value }),
    }),
  getRdsContext: (token: string, rdsId: number) =>
    apiFetch<{
      id: number;
      display_name: string;
      context_text: string;
      context_mode: string | null;
      effective_mode: string;
      default_mode: string;
    }>(`/api/admin/rds/${rdsId}/context`, { token }),
  putRdsContext: (
    token: string,
    rdsId: number,
    body: { context_text?: string | null; context_mode?: string | null },
  ) =>
    apiFetch<{ ok: boolean; effective_mode: string }>(`/api/admin/rds/${rdsId}/context`, {
      method: "PUT",
      token,
      body: JSON.stringify(body),
    }),

  // Auto-inject delivery behavior (default mode + default/custom pointer text)
  getContextBehavior: (token: string) =>
    apiFetch<ContextBehavior>("/api/admin/context-behavior", { token }),
  putContextBehavior: (
    token: string,
    body: { default_mode?: string; pointer_use_default?: boolean; pointer_custom_text?: string },
  ) =>
    apiFetch<ContextBehavior>("/api/admin/context-behavior", {
      method: "PUT",
      token,
      body: JSON.stringify(body),
    }),
  previewRdsContext: (token: string, rdsId: number) =>
    apiFetch<{
      rds_id: number;
      display_name: string;
      effective_mode: string;
      default_mode: string;
      full: { text: string; chars: number; tokens_est: number };
      pointer: { text: string; chars: number; tokens_est: number };
      active: { chars: number; tokens_est: number };
    }>(`/api/admin/rds/${rdsId}/context-preview`, { token }),

  // Machine agents (Account management → Agents)
  listAgents: (token: string) => apiFetch<Agent[]>("/api/admin/agents", { token }),
  createAgent: (token: string, body: { name: string; role_ids?: number[] | null }) =>
    apiFetch<AgentCreated>("/api/admin/agents", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),
  patchAgent: (token: string, id: number, body: { disabled?: boolean }) =>
    apiFetch<Agent>(`/api/admin/agents/${id}`, {
      method: "PATCH",
      token,
      body: JSON.stringify(body),
    }),
  deleteAgent: (token: string, id: number) =>
    apiFetch<{ deleted: boolean; id: number }>(`/api/admin/agents/${id}`, {
      method: "DELETE",
      token,
    }),
  rotateAgentClientSecret: (token: string, id: number) =>
    apiFetch<AgentCreated>(`/api/admin/agents/${id}/client-secret`, {
      method: "POST",
      token,
    }),
  listAgentTokens: (token: string, id: number) =>
    apiFetch<AgentToken[]>(`/api/admin/agents/${id}/tokens`, { token }),
  createAgentToken: (token: string, id: number, body: { label?: string | null }) =>
    apiFetch<AgentTokenCreated>(`/api/admin/agents/${id}/tokens`, {
      method: "POST",
      token,
      body: JSON.stringify(body),
    }),
  revokeAgentToken: (token: string, id: number, tokenId: number) =>
    apiFetch<{ revoked: boolean; id: number }>(`/api/admin/agents/${id}/tokens/${tokenId}`, {
      method: "DELETE",
      token,
    }),
};
